import { resolveLanguage } from '../../shared/language/_languages';
import { DemoQuestionView } from './demo.models';

export interface FallbackQuestion {
  id: number;
  text: { [lang: string]: string };
  options: { [lang: string]: string[] };
  correct: boolean[];
  hints: { [lang: string]: string[] };
}

function t(en: string, de: string, fr: string, it: string): { [lang: string]: string } {
  return { en, de, fr, it };
}

function opts(
  en: string[],
  de: string[],
  fr: string[],
  it: string[]
): { [lang: string]: string[] } {
  return { en, de, fr, it };
}

export const FALLBACK_DEMO_QUESTIONS: FallbackQuestion[] = [
  {
    id: 1,
    text: t(
      'The typical resting heart rate of a healthy adult is:',
      'Die typische Ruheherzfrequenz eines gesunden Erwachsenen liegt bei:',
      'La fréquence cardiaque au repos typique d’un adulte en bonne santé est :',
      'La frequenza cardiaca a riposo tipica di un adulto sano è:'
    ),
    options: opts(
      ['20–30 / min', '40–50 / min', '60–100 / min', '120–150 / min', '160–180 / min'],
      ['20–30 / min', '40–50 / min', '60–100 / min', '120–150 / min', '160–180 / min'],
      ['20–30 / min', '40–50 / min', '60–100 / min', '120–150 / min', '160–180 / min'],
      ['20–30 / min', '40–50 / min', '60–100 / min', '120–150 / min', '160–180 / min']
    ),
    correct: [false, false, true, false, false],
    hints: opts(
      ['Too low for a typical adult at rest.', 'Athletic bradycardia can occur, but 40–50 is not the typical range.', 'The usual resting range is about 60–100 beats per minute.', 'This is a tachycardic range.', 'This is a tachycardic range.'],
      ['Zu niedrig für einen typischen Erwachsenen in Ruhe.', 'Sportliche Bradykardie kommt vor, 40–50 ist aber nicht der typische Bereich.', 'Der übliche Ruhebereich liegt bei etwa 60–100 Schlägen pro Minute.', 'Das ist ein tachykarder Bereich.', 'Das ist ein tachykarder Bereich.'],
      ['Trop bas pour un adulte typique au repos.', 'Une bradycardie sportive peut exister, mais 40–50 n’est pas la plage typique.', 'La plage habituelle au repos est d’environ 60–100 battements par minute.', 'Il s’agit d’une plage tachycarde.', 'Il s’agit d’une plage tachycarde.'],
      ['Troppo bassa per un adulto tipico a riposo.', 'Può esserci bradicardia sportiva, ma 40–50 non è l’intervallo tipico.', 'L’intervallo abituale a riposo è circa 60–100 battiti al minuto.', 'È un intervallo tachicardico.', 'È un intervallo tachicardico.']
    )
  },
  {
    id: 2,
    text: t(
      'Which structure is the largest organ of the human body?',
      'Welche Struktur ist das größte Organ des menschlichen Körpers?',
      'Quelle structure est le plus grand organe du corps humain ?',
      'Quale struttura è l’organo più grande del corpo umano?'
    ),
    options: opts(
      ['Liver', 'Skin', 'Lung', 'Small intestine', 'Brain'],
      ['Leber', 'Haut', 'Lunge', 'Dünndarm', 'Gehirn'],
      ['Foie', 'Peau', 'Poumon', 'Intestin grêle', 'Cerveau'],
      ['Fegato', 'Pelle', 'Polmone', 'Intestino tenue', 'Cervello']
    ),
    correct: [false, true, false, false, false],
    hints: opts(
      ['The liver is the largest internal organ, not the largest overall.', 'Skin is the largest organ by surface area and weight.', 'Each lung is smaller than the skin.', 'The small intestine is long but not the largest organ.', 'The brain is smaller than the skin.'],
      ['Die Leber ist das größte innere Organ, nicht das größte insgesamt.', 'Die Haut ist nach Fläche und Gewicht das größte Organ.', 'Jede Lunge ist kleiner als die Haut.', 'Der Dünndarm ist lang, aber nicht das größte Organ.', 'Das Gehirn ist kleiner als die Haut.'],
      ['Le foie est le plus grand organe interne, pas le plus grand dans l’ensemble.', 'La peau est le plus grand organe en surface et en poids.', 'Chaque poumon est plus petit que la peau.', 'L’intestin grêle est long mais pas le plus grand organe.', 'Le cerveau est plus petit que la peau.'],
      ['Il fegato è l’organo interno più grande, non il più grande in assoluto.', 'La pelle è l’organo più grande per superficie e peso.', 'Ogni polmone è più piccolo della pelle.', 'L’intestino tenue è lungo ma non è l’organo più grande.', 'Il cervello è più piccolo della pelle.']
    )
  },
  {
    id: 3,
    text: t(
      'Insulin is produced primarily in the:',
      'Insulin wird vor allem gebildet in:',
      'L’insuline est produite principalement dans :',
      'L’insulina è prodotta principalmente nel:'
    ),
    options: opts(
      ['Liver', 'Adrenal cortex', 'Pancreatic beta cells', 'Thyroid', 'Spleen'],
      ['Leber', 'Nebennierenrinde', 'Betazellen des Pankreas', 'Schilddrüse', 'Milz'],
      ['Foie', 'Cortex surrénal', 'Cellules bêta du pancréas', 'Thyroïde', 'Rate'],
      ['Fegato', 'Corteccia surrenale', 'Cellule beta del pancreas', 'Tiroide', 'Milza']
    ),
    correct: [false, false, true, false, false],
    hints: opts(
      ['The liver responds to insulin; it does not produce it.', 'The adrenal cortex produces steroid hormones.', 'Beta cells in the islets of Langerhans secrete insulin.', 'The thyroid produces T3/T4 and calcitonin.', 'The spleen is not an endocrine source of insulin.'],
      ['Die Leber reagiert auf Insulin, bildet es aber nicht.', 'Die Nebennierenrinde bildet Steroidhormone.', 'Betazellen in den Langerhans-Inseln sezernieren Insulin.', 'Die Schilddrüse bildet T3/T4 und Calcitonin.', 'Die Milz ist keine endokrine Insulinquelle.'],
      ['Le foie répond à l’insuline ; il ne la produit pas.', 'Le cortex surrénal produit des hormones stéroïdes.', 'Les cellules bêta des îlots de Langerhans sécrètent l’insuline.', 'La thyroïde produit T3/T4 et de la calcitonine.', 'La rate n’est pas une source endocrine d’insuline.'],
      ['Il fegato risponde all’insulina; non la produce.', 'La corteccia surrenale produce ormoni steroidei.', 'Le cellule beta delle isole di Langerhans secernono insulina.', 'La tiroide produce T3/T4 e calcitonina.', 'La milza non è una fonte endocrina di insulina.']
    )
  },
  {
    id: 4,
    text: t(
      'Normal human core body temperature is approximately:',
      'Die normale menschliche Körperkerntemperatur beträgt etwa:',
      'La température corporelle centrale normale est d’environ :',
      'La temperatura corporea centrale normale è circa:'
    ),
    options: opts(
      ['32.0 °C', '35.0 °C', '37.0 °C', '39.5 °C', '41.0 °C'],
      ['32,0 °C', '35,0 °C', '37,0 °C', '39,5 °C', '41,0 °C'],
      ['32,0 °C', '35,0 °C', '37,0 °C', '39,5 °C', '41,0 °C'],
      ['32,0 °C', '35,0 °C', '37,0 °C', '39,5 °C', '41,0 °C']
    ),
    correct: [false, false, true, false, false],
    hints: opts(
      ['32 °C is severe hypothermia.', '35 °C is below the usual core range.', 'About 37 °C is the standard reference value.', '39.5 °C is fever.', '41 °C is high fever / hyperthermia.'],
      ['32 °C ist eine schwere Hypothermie.', '35 °C liegt unter dem üblichen Kernbereich.', 'Etwa 37 °C ist der übliche Referenzwert.', '39,5 °C ist Fieber.', '41 °C ist hohes Fieber / Hyperthermie.'],
      ['32 °C correspond à une hypothermie sévère.', '35 °C est sous la plage centrale habituelle.', 'Environ 37 °C est la valeur de référence habituelle.', '39,5 °C est une fièvre.', '41 °C est une forte fièvre / hyperthermie.'],
      ['32 °C è un’ipotermia grave.', '35 °C è sotto l’intervallo centrale abituale.', 'Circa 37 °C è il valore di riferimento standard.', '39,5 °C è febbre.', '41 °C è febbre alta / ipertermia.']
    )
  },
  {
    id: 5,
    text: t(
      'The bone of the upper arm is the:',
      'Der Knochen des Oberarms ist der:',
      'L’os du bras (bras proprement dit) est :',
      'L’osso del braccio (braccio propriamente detto) è:'
    ),
    options: opts(
      ['Radius', 'Ulna', 'Humerus', 'Femur', 'Tibia'],
      ['Radius', 'Ulna', 'Humerus', 'Femur', 'Tibia'],
      ['Radius', 'Ulna', 'Humérus', 'Fémur', 'Tibia'],
      ['Radio', 'Ulna', 'Omero', 'Femore', 'Tibia']
    ),
    correct: [false, false, true, false, false],
    hints: opts(
      ['The radius is a forearm bone.', 'The ulna is a forearm bone.', 'The humerus is the single bone of the upper arm.', 'The femur is the thigh bone.', 'The tibia is a leg bone.'],
      ['Der Radius ist ein Unterarmknochen.', 'Die Ulna ist ein Unterarmknochen.', 'Der Humerus ist der einzige Knochen des Oberarms.', 'Der Femur ist der Oberschenkelknochen.', 'Die Tibia ist ein Unterschenkelknochen.'],
      ['Le radius est un os de l’avant-bras.', 'L’ulna est un os de l’avant-bras.', 'L’humérus est l’os unique du bras.', 'Le fémur est l’os de la cuisse.', 'Le tibia est un os de la jambe.'],
      ['Il radio è un osso dell’avambraccio.', 'L’ulna è un osso dell’avambraccio.', 'L’omero è l’unico osso del braccio.', 'Il femore è l’osso della coscia.', 'La tibia è un osso della gamba.']
    )
  },
  {
    id: 6,
    text: t(
      'The oxygen-carrying protein in red blood cells is:',
      'Das sauerstoffbindende Protein in den Erythrozyten ist:',
      'La protéine transporteuse d’oxygène des globules rouges est :',
      'La proteina che trasporta l’ossigeno nei globuli rossi è:'
    ),
    options: opts(
      ['Myoglobin', 'Albumin', 'Hemoglobin', 'Fibrinogen', 'Ferritin'],
      ['Myoglobin', 'Albumin', 'Hämoglobin', 'Fibrinogen', 'Ferritin'],
      ['Myoglobine', 'Albumine', 'Hémoglobine', 'Fibrinogène', 'Ferritine'],
      ['Mioglobina', 'Albumina', 'Emoglobina', 'Fibrinogeno', 'Ferritina']
    ),
    correct: [false, false, true, false, false],
    hints: opts(
      ['Myoglobin stores oxygen in muscle.', 'Albumin is the main plasma protein.', 'Hemoglobin in erythrocytes binds oxygen.', 'Fibrinogen is a clotting protein.', 'Ferritin stores iron.'],
      ['Myoglobin speichert Sauerstoff im Muskel.', 'Albumin ist das wichtigste Plasmaprotein.', 'Hämoglobin in Erythrozyten bindet Sauerstoff.', 'Fibrinogen ist ein Gerinnungsprotein.', 'Ferritin speichert Eisen.'],
      ['La myoglobine stocke l’oxygène dans le muscle.', 'L’albumine est la principale protéine plasmatique.', 'L’hémoglobine des érythrocytes lie l’oxygène.', 'Le fibrinogène est une protéine de coagulation.', 'La ferritine stocke le fer.'],
      ['La mioglobina immagazzina ossigeno nel muscolo.', 'L’albumina è la principale proteina plasmatica.', 'L’emoglobina negli eritrociti lega l’ossigeno.', 'Il fibrinogeno è una proteina della coagulazione.', 'La ferritina immagazzina ferro.']
    )
  },
  {
    id: 7,
    text: t(
      'Vitamin C deficiency causes:',
      'Ein Vitamin-C-Mangel verursacht:',
      'Une carence en vitamine C provoque :',
      'Una carenza di vitamina C causa:'
    ),
    options: opts(
      ['Rickets', 'Scurvy', 'Pellagra', 'Beriberi', 'Night blindness'],
      ['Rachitis', 'Skorbut', 'Pellagra', 'Beriberi', 'Nachtblindheit'],
      ['Rachitisme', 'Scorbut', 'Pellagre', 'Béribéri', 'Héméralopie'],
      ['Rachitismo', 'Scorbuto', 'Pellagra', 'Beriberi', 'Cecità notturna']
    ),
    correct: [false, true, false, false, false],
    hints: opts(
      ['Rickets is caused by vitamin D deficiency.', 'Scurvy is the classic vitamin C deficiency disease.', 'Pellagra is niacin (B3) deficiency.', 'Beriberi is thiamine (B1) deficiency.', 'Night blindness is associated with vitamin A deficiency.'],
      ['Rachitis entsteht durch Vitamin-D-Mangel.', 'Skorbut ist die klassische Vitamin-C-Mangelerkrankung.', 'Pellagra entsteht durch Niacin-(B3-)Mangel.', 'Beriberi entsteht durch Thiamin-(B1-)Mangel.', 'Nachtblindheit hängt mit Vitamin-A-Mangel zusammen.'],
      ['Le rachitisme est dû à une carence en vitamine D.', 'Le scorbut est la maladie classique de carence en vitamine C.', 'La pellagre est une carence en niacine (B3).', 'Le béribéri est une carence en thiamine (B1).', 'L’héméralopie est liée à une carence en vitamine A.'],
      ['Il rachitismo è causato da carenza di vitamina D.', 'Lo scorbuto è la malattia classica da carenza di vitamina C.', 'La pellagra è una carenza di niacina (B3).', 'Il beriberi è una carenza di tiamina (B1).', 'La cecità notturna è associata a carenza di vitamina A.']
    )
  },
  {
    id: 8,
    text: t(
      'The primary pacemaker of the heart is the:',
      'Der primäre Schrittmacher des Herzens ist der:',
      'Le pacemaker primaire du cœur est :',
      'Il pacemaker primario del cuore è:'
    ),
    options: opts(
      ['AV node', 'Sinoatrial (SA) node', 'Bundle of His', 'Purkinje fibers', 'Left ventricle'],
      ['AV-Knoten', 'Sinusknoten (SA-Knoten)', 'His-Bündel', 'Purkinje-Fasern', 'Linker Ventrikel'],
      ['Nœud AV', 'Nœud sinusal (SA)', 'Faisceau de His', 'Fibres de Purkinje', 'Ventricule gauche'],
      ['Nodo AV', 'Nodo senoatriale (SA)', 'Fascio di His', 'Fibre di Purkinje', 'Ventricolo sinistro']
    ),
    correct: [false, true, false, false, false],
    hints: opts(
      ['The AV node delays conduction; it is a secondary pacemaker.', 'The SA node normally initiates each heartbeat.', 'The His bundle conducts from AV node to ventricles.', 'Purkinje fibers distribute the impulse in the ventricles.', 'Ventricular muscle is not the primary pacemaker.'],
      ['Der AV-Knoten verzögert die Leitung; er ist ein sekundärer Schrittmacher.', 'Der Sinusknoten löst normalerweise jeden Herzschlag aus.', 'Das His-Bündel leitet vom AV-Knoten zu den Ventrikeln.', 'Purkinje-Fasern verteilen den Impuls in den Ventrikeln.', 'Der Ventrikelmuskel ist nicht der primäre Schrittmacher.'],
      ['Le nœud AV retarde la conduction ; c’est un pacemaker secondaire.', 'Le nœud sinusal initie normalement chaque battement.', 'Le faisceau de His conduit du nœud AV aux ventricules.', 'Les fibres de Purkinje distribuent l’influx dans les ventricules.', 'Le muscle ventriculaire n’est pas le pacemaker primaire.'],
      ['Il nodo AV ritarda la conduzione; è un pacemaker secondario.', 'Il nodo SA avvia normalmente ogni battito.', 'Il fascio di His conduce dal nodo AV ai ventricoli.', 'Le fibre di Purkinje distribuiscono l’impulso nei ventricoli.', 'Il muscolo ventricolare non è il pacemaker primario.']
    )
  },
  {
    id: 9,
    text: t(
      'Which of the following are functions of the liver? (select all that apply)',
      'Welche der folgenden sind Funktionen der Leber? (alle zutreffenden wählen)',
      'Lesquelles des propositions suivantes sont des fonctions du foie ? (plusieurs réponses possibles)',
      'Quali delle seguenti sono funzioni del fegato? (selezionare tutte quelle corrette)'
    ),
    options: opts(
      ['Bile production', 'Detoxification of many drugs', 'Glycogen storage', 'Insulin secretion', 'Synthesis of many plasma proteins'],
      ['Galleproduktion', 'Entgiftung vieler Arzneimittel', 'Glykogenspeicherung', 'Insulinsekretion', 'Synthese vieler Plasmaproteine'],
      ['Production de bile', 'Détoxification de nombreux médicaments', 'Stockage du glycogène', 'Sécrétion d’insuline', 'Synthèse de nombreuses protéines plasmatiques'],
      ['Produzione di bile', 'Detossificazione di molti farmaci', 'Deposito di glicogeno', 'Secrezione di insulina', 'Sintesi di molte proteine plasmatiche']
    ),
    correct: [true, true, true, false, true],
    hints: opts(
      ['Hepatocytes produce bile.', 'The liver metabolizes many xenobiotics.', 'The liver stores glycogen.', 'Insulin is secreted by pancreatic beta cells, not the liver.', 'The liver synthesizes albumin and many clotting factors.'],
      ['Hepatozyten bilden Galle.', 'Die Leber metabolisiert viele Xenobiotika.', 'Die Leber speichert Glykogen.', 'Insulin wird von pankreatischen Betazellen gebildet, nicht von der Leber.', 'Die Leber synthetisiert Albumin und viele Gerinnungsfaktoren.'],
      ['Les hépatocytes produisent la bile.', 'Le foie métabolise de nombreux xénobiotiques.', 'Le foie stocke le glycogène.', 'L’insuline est sécrétée par les cellules bêta pancréatiques, pas par le foie.', 'Le foie synthétise l’albumine et de nombreux facteurs de coagulation.'],
      ['Gli epatociti producono bile.', 'Il fegato metabolizza molti xenobiotici.', 'Il fegato deposita glicogeno.', 'L’insulina è secreta dalle cellule beta pancreatiche, non dal fegato.', 'Il fegato sintetizza albumina e molti fattori della coagulazione.']
    )
  },
  {
    id: 10,
    text: t(
      'Antibiotics are effective against which of the following? (select all that apply)',
      'Antibiotika sind wirksam gegen welche der folgenden Erreger? (alle zutreffenden wählen)',
      'Les antibiotiques sont efficaces contre lesquels des agents suivants ? (plusieurs réponses possibles)',
      'Gli antibiotici sono efficaci contro quali dei seguenti agenti? (selezionare tutte quelle corrette)'
    ),
    options: opts(
      ['Typical bacteria', 'Viruses such as influenza', 'Fungi such as Candida', 'Intracellular bacteria (selected agents)', 'Prions'],
      ['Typische Bakterien', 'Viren wie Influenza', 'Pilze wie Candida', 'Intrazelluläre Bakterien (ausgewählte Wirkstoffe)', 'Prionen'],
      ['Bactéries typiques', 'Virus tels que la grippe', 'Champignons tels que Candida', 'Bactéries intracellulaires (certains agents)', 'Prions'],
      ['Batteri tipici', 'Virus come l’influenza', 'Funghi come Candida', 'Batteri intracellulari (alcuni agenti)', 'Prioni']
    ),
    correct: [true, false, false, true, false],
    hints: opts(
      ['Antibiotics target bacterial structures or pathways.', 'Viruses are not treated with antibiotics.', 'Fungi require antifungals, not antibiotics.', 'Some antibiotics (e.g. macrolides, tetracyclines) cover selected intracellular bacteria.', 'Prions are not treated with antibiotics.'],
      ['Antibiotika zielen auf bakterielle Strukturen oder Stoffwechselwege.', 'Viren werden nicht mit Antibiotika behandelt.', 'Pilze erfordern Antimykotika, keine Antibiotika.', 'Einige Antibiotika (z. B. Makrolide, Tetrazykline) erfassen ausgewählte intrazelluläre Bakterien.', 'Prionen werden nicht mit Antibiotika behandelt.'],
      ['Les antibiotiques ciblent des structures ou voies bactériennes.', 'Les virus ne se traitent pas par antibiotiques.', 'Les champignons nécessitent des antifongiques, pas des antibiotiques.', 'Certains antibiotiques (macrolides, cyclines) couvrent des bactéries intracellulaires sélectionnées.', 'Les prions ne se traitent pas par antibiotiques.'],
      ['Gli antibiotici agiscono su strutture o vie batteriche.', 'I virus non si trattano con antibiotici.', 'I funghi richiedono antimicotici, non antibiotici.', 'Alcuni antibiotici (es. macrolidi, tetracicline) coprono batteri intracellulari selezionati.', 'I prioni non si trattano con antibiotici.']
    )
  }
];

export function localizeFallbackQuestion(question: FallbackQuestion, lang: string | null): DemoQuestionView {
  const code = resolveLanguage(lang);
  const text = question.text[code] || question.text.en;
  const options = question.options[code] || question.options.en;
  return {
    id: question.id,
    questionText: text,
    image: '',
    options,
    sourceName: '',
    sourceLink: '',
    nextQuestion: null,
    previousQuestion: null
  };
}

export function fallbackHints(question: FallbackQuestion, lang: string | null): string[] {
  const code = resolveLanguage(lang);
  return question.hints[code] || question.hints.en;
}
